import { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeftRight, Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useSEO";

const Comparator = () => {
  useSEO({
    title: "Comparador de patinetes eléctricos — Compara hasta 3 modelos",
    description: "Compara características, precio, marca y homologación DGT de hasta 3 patinetes eléctricos a la vez. Toma la mejor decisión de compra.",
    canonical: "/comparador",
  });
  const [listings, setListings] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    supabase
      .from("listings")
      .select("id, title, brand, model, price, condition, city, dgt_approved, images")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(100)
      .then(({ data }) => setListings(data || []));
  }, []);

  const addToCompare = (id: string) => {
    if (selected.length >= 3) return;
    const item = listings.find((l) => l.id === id);
    if (item && !selected.find((s) => s.id === id)) {
      setSelected([...selected, item]);
    }
  };

  const removeFromCompare = (id: string) => {
    setSelected(selected.filter((s) => s.id !== id));
  };

  const conditionLabel: Record<string, string> = { nuevo: "Nuevo", usado: "Usado", recambios: "Recambios" };
  const available = listings.filter(
    (l) => !selected.find((s) => s.id === l.id) && 
    (searchTerm === "" || l.title.toLowerCase().includes(searchTerm.toLowerCase()) || l.brand.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const compareFields = [
    { key: "brand", label: "Marca" },
    { key: "model", label: "Modelo" },
    { key: "price", label: "Precio", format: (v: any) => `${Number(v).toLocaleString("es-ES")} €` },
    { key: "condition", label: "Estado", format: (v: string) => conditionLabel[v] || v },
    { key: "city", label: "Ciudad" },
    { key: "dgt_approved", label: "Homologado DGT", format: (v: boolean) => v ? "✅ Sí" : "❌ No" },
  ];

  return (
    <Layout>
      <section className="py-10">
        <div className="container max-w-5xl">
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="h-7 w-7" /> Comparador de patinetes
          </h1>
          <p className="mt-2 text-muted-foreground">Selecciona hasta 3 patinetes para comparar sus características</p>

          {/* Selector */}
          <Card className="mt-6">
            <CardContent className="p-4">
              <div className="flex gap-3 items-end flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1 block">Añadir patinete</label>
                  <Select onValueChange={addToCompare} disabled={selected.length >= 3}>
                    <SelectTrigger>
                      <SelectValue placeholder={selected.length >= 3 ? "Máximo 3 patinetes" : "Buscar patinete..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {available.slice(0, 20).map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.brand} {l.model} — {Number(l.price).toLocaleString("es-ES")} €
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparison table */}
          {selected.length > 0 ? (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground border-b border-border w-36"></th>
                    {selected.map((s) => (
                      <th key={s.id} className="p-3 border-b border-border min-w-[200px]">
                        <div className="relative">
                          <button 
                            onClick={() => removeFromCompare(s.id)} 
                            className="absolute -top-1 -right-1 rounded-full bg-destructive/10 p-1 hover:bg-destructive/20"
                          >
                            <X className="h-3 w-3 text-destructive" />
                          </button>
                          {s.images?.[0] && (
                            <img src={s.images[0]} alt={s.title} className="h-32 w-full object-cover rounded-lg mb-2" />
                          )}
                          <p className="font-display font-semibold text-sm">{s.title}</p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareFields.map((field) => (
                    <tr key={field.key} className="border-b border-border">
                      <td className="p-3 text-sm font-medium text-muted-foreground">{field.label}</td>
                      {selected.map((s) => {
                        const val = (s as any)[field.key];
                        const displayVal = field.format ? field.format(val) : val;
                        // Highlight best price
                        const isBestPrice = field.key === "price" && selected.length > 1 &&
                          Number(s.price) === Math.min(...selected.map((x) => Number(x.price)));
                        return (
                          <td key={s.id} className={`p-3 text-sm ${isBestPrice ? "font-bold text-primary" : ""}`}>
                            {displayVal}
                            {isBestPrice && <Badge className="ml-2 text-[10px]">Mejor precio</Badge>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-12 text-center py-12">
              <ArrowLeftRight className="h-12 w-12 mx-auto text-muted-foreground/30" />
              <p className="mt-4 text-muted-foreground">Selecciona patinetes para empezar a comparar</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Comparator;
